import React, { useState, useEffect } from 'react';
import { apiService, RankingSnapshotsResponse } from '../services/api';

interface SnapshotSelectorProps {
  selectedSnapshot: string | null;
  onSnapshotChange: (snapshot: string) => void;
  onValidated?: (isValid: boolean) => void;
  label?: string;
  disabled?: boolean;
}

const SnapshotSelector: React.FC<SnapshotSelectorProps> = ({
  selectedSnapshot,
  onSnapshotChange,
  onValidated,
  label = 'Seleccionar Snapshot',
  disabled = false
}) => {
  const [snapshots, setSnapshots] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    const fetchSnapshots = async () => {
      try {
        setLoading(true);
        setError(null);
        const response: RankingSnapshotsResponse = await apiService.getRankingSnapshots({ limit: 50 });
        setSnapshots(response.snapshots);
      } catch (err) {
        console.error('Error fetching snapshots:', err);
        setError('Erro ao carregar snapshots');
      } finally {
        setLoading(false);
      }
    };

    fetchSnapshots();
  }, []);

  // Quando snapshots carregados, verificar se selectedSnapshot existe
  // Se não existir, procurar o mais próximo via API
  useEffect(() => {
    if (snapshots.length === 0 || !selectedSnapshot) return;

    // Verificar se existe na lista (comparar sem timezone para maior flexibilidade)
    const normalizeTimestamp = (ts: string) => ts.replace(/\+00:00$/, '').replace(/Z$/, '');
    const normalizedSelected = normalizeTimestamp(selectedSnapshot);
    const existsInList = snapshots.some(s => normalizeTimestamp(s) === normalizedSelected);

    if (existsInList) {
      // Snapshot existe na lista - marcar como validado
      onValidated?.(true);
      return;
    }

    // Snapshot não está na lista - verificar via API se existe no BigQuery
    const validateSnapshot = async () => {
      setIsValidating(true);
      onValidated?.(false);

      try {
        const result = await apiService.getNearestSnapshot(selectedSnapshot);
        if (result.nearest) {
          const normalizedNearest = normalizeTimestamp(result.nearest);

          // Verificar se a API retornou o mesmo snapshot (existe no BigQuery)
          if (normalizedNearest === normalizedSelected) {
            // Snapshot existe no BigQuery (apenas não está nos últimos 50)
            // Marcar como validado SEM mudar o snapshot
            console.log(`Snapshot ${selectedSnapshot} validado via API (existe no BigQuery)`);
            onValidated?.(true);
          } else {
            // API retornou um snapshot diferente - usar o mais próximo
            console.log(`Snapshot ${selectedSnapshot} não existe, usando mais próximo: ${result.nearest}`);
            onSnapshotChange(result.nearest);
            // onValidated será chamado quando o novo snapshot for validado no próximo ciclo
          }
        } else {
          // API não encontrou nenhum - usar o primeiro da lista
          console.log(`Snapshot ${selectedSnapshot} não encontrado, usando primeiro da lista: ${snapshots[0]}`);
          onSnapshotChange(snapshots[0]);
        }
      } catch (error) {
        console.error('Erro ao validar snapshot:', error);
        // Fallback: usar o primeiro da lista
        onSnapshotChange(snapshots[0]);
      } finally {
        setIsValidating(false);
      }
    };

    validateSnapshot();
  }, [snapshots, selectedSnapshot, onSnapshotChange, onValidated]);

  const formatSnapshotDateTime = (timestamp: string): string => {
    try {
      // Converter UTC para Europe/Lisbon para exibição
      const date = new Date(timestamp);
      return date.toLocaleString('pt-PT', {
        timeZone: 'Europe/Lisbon',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch {
      return timestamp;
    }
  };

  const formatSnapshotDate = (timestamp: string): string => {
    try {
      // Converter UTC para Europe/Lisbon para obter a data correta
      const date = new Date(timestamp);
      return date.toLocaleDateString('pt-PT', {
        timeZone: 'Europe/Lisbon',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return timestamp;
    }
  };

  const groupSnapshotsByDate = (snapshots: string[]) => {
    const grouped: { [date: string]: string[] } = {};
    
    snapshots.forEach(snapshot => {
      const date = formatSnapshotDate(snapshot);
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(snapshot);
    });

    return grouped;
  };

  if (loading) {
    return (
      <div className="mb-3">
        <label className="form-label">{label}</label>
        <select className="form-select" disabled>
          <option>A carregar snapshots...</option>
        </select>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-3">
        <label className="form-label">{label}</label>
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  const groupedSnapshots = groupSnapshotsByDate(snapshots);

  return (
    <div className="mb-3">
      <label className="form-label">{label}</label>
      <select 
        className="form-select"
        value={selectedSnapshot || ''}
        onChange={(e) => onSnapshotChange(e.target.value)}
        disabled={disabled || snapshots.length === 0}
      >
        <option value="">Seleccionar...</option>
        {Object.entries(groupedSnapshots)
          // Ordenar grupos pelo timestamp ISO mais recente de cada dia (desc).
          // NÃO usar a chave `date` (formato "DD/MM/YYYY") — localeCompare sobre
          // essa string ordena pelo dia, pondo 30/06 acima de 02/07.
          .sort(([, aSnaps], [, bSnaps]) => bSnaps[0].localeCompare(aSnaps[0]))
          .map(([date, dateSnapshots]) => (
            <optgroup key={date} label={date}>
              {dateSnapshots
                .sort((a, b) => b.localeCompare(a)) // Sort times descending
                .map((snapshot) => (
                  <option key={snapshot} value={snapshot}>
                    {formatSnapshotDateTime(snapshot)}
                  </option>
                ))}
            </optgroup>
          ))}
      </select>
      {snapshots.length === 0 && (
        <div className="form-text text-muted">
          Nenhum snapshot disponível
        </div>
      )}
      {isValidating && (
        <div className="form-text text-warning">
          <i className="bi bi-hourglass-split me-1"></i>
          A procurar snapshot mais próximo...
        </div>
      )}
      {selectedSnapshot && !isValidating && (
        <div className="form-text">
          <strong>Selecionado:</strong> {formatSnapshotDateTime(selectedSnapshot)}
        </div>
      )}
    </div>
  );
};

export default SnapshotSelector;